import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateTime } from 'luxon'

import {
  selectIsNotesTab,
  selectTransactionFilters,
} from '@/reducers/finances/transactionFiltersReducer'
import { UserWithAdminInfo } from '@/reducers/admin/v2/allUsersReducerV2'
import {
  selectCurrentAnnualTaxYear,
  selectTaxDetailsByYear,
} from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { Transaction } from '@/reducers/admin/allTransactions.slice'
import {
  TransactionFilter,
  countTransactions,
  fetchPaginatedTransactions,
  fetchTransaction,
} from '@/actions/admin/v2/adminTransactionActions'
import { fetchUserFinancialAccounts } from '@/actions/admin/adminFinancialAccountActions'
import { fetchTransactionCounts } from '@/actions/adminActions'
import {
  DATE_FORMATS_LUXON,
  convertUtcToLocalDate,
  filterDateToIso,
} from '@/utils/dateHelpers'
import {
  formatAccountName,
  isTransactionLocked,
} from '@/features/transactions/helpers'
import { useAsync, useAsyncCallback, useReselector } from '@/utils/sharedHooks'
import { filterNulls, useAppDispatch } from '@/utils/typeHelpers'
import { PrismaCursorPagination } from '../../../utils/fetchHelpers'
import { centsToDollars } from '@/utils/currencyHelpers'
import { Alert, Text } from '@/componentsV1/BaseComponents'
import {
  PaginationStyle,
  useCursorPaginatedTable,
} from '@/features/admin/paginated-table/CursorPaginatedTable'
import {
  OrderBy,
  TypeColumn,
} from '@/features/admin/paginated-table/AdminBaseTable'
import { CurrencyFormatLabel } from '@/components/CurrencyFormatLabel'
import TransactionTypeButton from '@/features/admin/shared/TransactionTypeButton'
import TransactionCategoryDropdown from '@/features/admin/shared/TransactionCategoryDropdown'
import TransactionFilters from '../TransactionFilters'
import RowActions from './RowActions'
import RowDrawer from './RowDrawer'
import RowNotes from './RowNotes'
import RowSelector from './RowSelector'
import RowTags from './RowTags'
import BulkTransactionActions, {
  OnActionParams,
} from '../shared/BulkTransactionActions'
import { getSelectedTransactionIds } from '@/reducers/admin/selectedTransactionsReducer'
import TransactionHeaderCheckbox from '../shared/TransactionHeaderCheckbox'
import AdminExportTransactionCsv from '../AdminExportTransactionCsv'
import { fetchAdminAllAnnualTaxDetailsIfNeeded } from '../annual-tax-details/annualTaxDetails.slice'

export type TransactionTableProps = {
  user: UserWithAdminInfo
  onTransactionChange?: (transactionIds: number[]) => void
}

export const PaginatedTransactionTable = ({
  user,
  onTransactionChange,
}: TransactionTableProps) => {
  const dispatch = useAppDispatch()
  const transactionFilters = useReselector(selectTransactionFilters)
  const isNotesTab = useReselector(selectIsNotesTab)
  const currentTaxYear = useReselector(selectCurrentAnnualTaxYear)
  const annualTaxDetails = useReselector(selectTaxDetailsByYear, currentTaxYear)
  const [filter, setFilter] = useState<TransactionFilter>({})
  const [selected, setSelected] = useState<Transaction[]>([])
  const selectedTransactionKeys = useReselector(getSelectedTransactionIds)

  // Tab count badges are read from Redux by TransactionFilters, which only fetches them on
  // mount. Refresh them after any in-place mutation so counts (e.g. Auto-confirmed) stay in
  // sync with the live list as a bookkeeper confirms/recategorizes rows.
  const refreshTransactionCounts = useCallback(() => {
    const toDateTime = (value: string | null | undefined) => {
      if (!value) return undefined
      const parsed = DateTime.fromFormat(value, DATE_FORMATS_LUXON.API_INPUT)
      return parsed.isValid ? parsed : undefined
    }
    fetchTransactionCounts({
      userId: user.id,
      startDate: toDateTime(transactionFilters.startDate),
      endDate: toDateTime(transactionFilters.endDate),
    })(dispatch)
  }, [
    dispatch,
    user.id,
    transactionFilters.startDate,
    transactionFilters.endDate,
  ])

  const {
    loading: noteCountPending,
    error: noteCountError,
    result: noteCount,
  } = useAsync(
    useCallback(
      async () => (isNotesTab ? await countTransactions(filter) : 0),
      [filter, isNotesTab]
    )
  )

  useEffect(() => {
    // Needed to fetch financial accounts for filter dropdown
    dispatch(fetchUserFinancialAccounts(user.id))
    dispatch(fetchAdminAllAnnualTaxDetailsIfNeeded())
  }, [dispatch, user.id])

  const reviewDate =
    convertUtcToLocalDate(annualTaxDetails?.eoy_survey_start_date)?.toFormat(
      DATE_FORMATS_LUXON.GUSTO_SUBMIT
    ) ||
    DateTime.utc().minus({ days: 7 }).toFormat(DATE_FORMATS_LUXON.GUSTO_SUBMIT)

  const cols: TypeColumn<Transaction>[] = [
    {
      header: {
        key: 'selector',
        render: () => <TransactionHeaderCheckbox transactions={transactions} />,
      },
      accessor: null,
      render: (row) => <RowSelector row={row} user={user} />,
    },
    {
      header: 'Id',
      accessor: 'id',
      width: '1',
      sortColumn: 'id',
    },
    {
      header: 'Date',
      accessor: null,
      render: (row) =>
        convertUtcToLocalDate(row.date)?.toFormat(
          DATE_FORMATS_LUXON.DISPLAY_SHORT
        ) || '',
      width: '2',
      sortColumn: 'date',
    },
    {
      header: 'Account',
      accessor: null,
      render: (row) => (
        <span>
          {row.financialAccount
            ? formatAccountName(row.financialAccount)
            : 'N/A'}
        </span>
      ),
      width: '3',
      sortColumn: 'financialAccount',
    },
    {
      header: 'Amount',
      accessor: null,
      render: (row) => (
        <CurrencyFormatLabel value={centsToDollars(row.amountInCents)} />
      ),
      width: '1',
      sortColumn: 'amountInCents',
    },
    {
      header: 'Description',
      accessor: 'description',
      width: '6',
      sortColumn: 'description',
    },
    {
      header: 'Notes',
      accessor: null,
      render: (row) => <RowNotes transaction={row} />,
      width: '5',
      sortColumn: 'notes',
    },
    {
      header: 'Expense Type',
      accessor: null,
      render: (row) => (
        <TransactionTypeButton
          transaction={row}
          onUpdate={(transaction) => {
            updateTransactions([transaction])
            refreshTransactionCounts()
          }}
        />
      ),
      width: '4',
    },
    {
      header: 'Category',
      accessor: null,
      render: (row) => (
        <TransactionCategoryDropdown
          transaction={row}
          onUpdate={(transaction) => {
            updateTransactions([transaction])
            refreshTransactionCounts()
          }}
        />
      ),
      width: '4',
      sortColumn: 'transactionCategory',
    },
    {
      header: 'Tags',
      accessor: null,
      render: (row) => <RowTags row={row} />,
      width: '4',
    },
    {
      header: 'Actions',
      accessor: null,
      render: (row) => (
        <RowActions
          row={row}
          onTransactionUpdate={onTransactionUpdate.callback}
          onTransactionDelete={(transaction) => {
            removeTransactions([transaction])
            refreshTransactionCounts()
          }}
        />
      ),
      width: '3',
    },
  ]

  const relationsToSelect = useMemo(
    () => ['financialAccount', 'receiptCount'],
    []
  )

  const fetchData = useCallback(
    (pagination: PrismaCursorPagination, orderBy?: OrderBy) => {
      const asyncFetchData = () => {
        const transactionCategoryId =
          transactionFilters.transactionCategoryId &&
          transactionFilters.transactionCategoryId !== '1'
            ? Number(transactionFilters.transactionCategoryId)
            : undefined
        const transactionsType =
          transactionFilters.transactionsType !== 'all'
            ? transactionFilters.transactionsType
            : undefined
        const startDate = filterDateToIso(transactionFilters.startDate, 'start')
        const endDate = filterDateToIso(transactionFilters.endDate, 'end')

        const filter: TransactionFilter = {
          userId: user.id,
          transactionsType,
          transactionCategoryId,
          financialAccountId:
            transactionFilters.financialAccountId ?? undefined,
          startDate: startDate && !isNotesTab ? startDate : undefined,
          endDate: endDate && !isNotesTab ? endDate : undefined,
          notesLastUpdatedAtStart:
            startDate && isNotesTab ? startDate : undefined,
          notesLastUpdatedAtEnd: endDate && isNotesTab ? endDate : undefined,
          includePersonal: transactionFilters.transactionsType === 'excluded',
          descriptionSearch: transactionFilters.description ?? undefined,
          notesSearch: transactionFilters.notes ?? undefined,
        }

        if (transactionsType === 'recentlyUpdated') {
          filter.currentTaxYear = currentTaxYear
          filter.lastReviewDate = reviewDate
        }

        setFilter(filter)

        return fetchPaginatedTransactions({
          filter,
          pagination,
          orderBy,
          select: relationsToSelect,
        })
      }

      return asyncFetchData()
    },
    [
      user.id,
      transactionFilters,
      currentTaxYear,
      reviewDate,
      isNotesTab,
      relationsToSelect,
    ]
  )

  const tableRowPropsConfig = {
    negative: (row: Transaction) => !row.type,
    positive: (row: Transaction) => row.confirmedAt !== null,
    warning: (row: Transaction) => Boolean(row.type && !row.confirmedAt),
    disabled: (row: Transaction) =>
      isTransactionLocked({
        date: row.date,
        userLockedYear: user.booksLockedForUserYear,
        adminLockedYear: user.booksLockedForAdminYear,
        isAdmin: true,
      }),
    renderDrawer: (row: Transaction, closeDrawer: () => void) => (
      <RowDrawer
        row={row}
        colSpan={cols.length + 1}
        onTransactionUpdate={onTransactionUpdate.callback}
        closeDrawer={closeDrawer}
      />
    ),
  }

  const {
    renderTable,
    items: transactions,
    getItem: getTransaction,
    updateItems: updateTransactions,
    removeItems: removeTransactions,
    fetchError,
    resetPagination,
  } = useCursorPaginatedTable<Transaction>({
    columns: cols,
    tableRowPropsConfig,
    fetchData,
    paginationStyle: PaginationStyle.LoadMore,
    paginationProps: { take: 50 },
    wrapperStyle: { minHeight: '400px' },
    initialOrderBy: { column: 'date', direction: 'descending' },
  })

  // Some bulk actions need the full transaction object
  useEffect(() => {
    const selectTransactions = filterNulls(
      selectedTransactionKeys.map(getTransaction)
    )
    setSelected(selectTransactions)
  }, [selectedTransactionKeys, getTransaction])

  const onTransactionUpdate = useAsyncCallback(
    useCallback(
      async (id: number) => {
        const transaction = await fetchTransaction({
          id,
          select: relationsToSelect,
        })
        if (transaction) {
          onTransactionChange?.([transaction.id])
          updateTransactions([transaction])
          refreshTransactionCounts()
        }
      },
      [
        updateTransactions,
        relationsToSelect,
        onTransactionChange,
        refreshTransactionCounts,
      ]
    ),
    false
  )

  const onBulkAction = (params: OnActionParams) => {
    if (params.type === 'delete') {
      removeTransactions(params.transactionIds)
      onTransactionChange?.(params.transactionIds)
    } else {
      updateTransactions(params.transactions)
      onTransactionChange?.(
        params.transactions.map((transaction) => transaction.id)
      )
    }
    refreshTransactionCounts()
  }

  return (
    <div style={{ marginBottom: '10px', overflowX: 'scroll' }}>
      <AdminExportTransactionCsv fetchData={fetchData} />

      <TransactionFilters
        userId={user.id}
        onFilterChange={() => resetPagination()}
        supportV2
      />
      <BulkTransactionActions
        selectedTransactions={selected}
        onAction={onBulkAction}
        userId={user.id}
      />

      {isNotesTab && !noteCountPending && (
        <Text as="h3">
          Notes left after confirmation{' '}
          {noteCount && noteCount > 0 ? `(${noteCount})` : ''}
        </Text>
      )}

      {(fetchError || noteCountError || onTransactionUpdate.error) && (
        <Alert
          type="error"
          title={
            fetchError?.message ??
            noteCountError?.message ??
            onTransactionUpdate?.error?.message
          }
        />
      )}
      {renderTable()}
    </div>
  )
}
